import type * as React from "react";

import {
  concat,
  filter,
  first,
  includes,
  isArray,
  isNil,
  last,
  range,
  some,
  uniq,
} from "../lib/utils";
import YearView from "../views/YearView";
import {
  type BasePickerOnChangeData,
  type BasePickerProps,
  type DisableValuesProps,
  type EnableValuesProps,
  type MinMaxValueProps,
  SingleSelectionPicker,
} from "./BasePicker";

const PAGE_WIDTH = 3;
const PAGE_HEIGHT = 4;
const YEARS_ON_PAGE = PAGE_WIDTH * PAGE_HEIGHT;

type YearPickerProps = BasePickerProps &
  DisableValuesProps &
  EnableValuesProps &
  MinMaxValueProps;

export type { YearPickerProps };

export interface YearPickerOnChangeData extends BasePickerOnChangeData {
  value: { year: number };
}

class YearPicker extends SingleSelectionPicker<YearPickerProps> {
  /*
    Note:
      use it like this <YearPicker key={someInputValue} />
      to make react create new instance when input value changes
  */
  constructor(props: YearPickerProps) {
    super(props);
    this.PAGE_WIDTH = PAGE_WIDTH;
  }

  public render() {
    const {
      inline,
      onCalendarViewMount,
      localization,
      onHeaderClick,
      pickerWidth,
      pickerStyle,
    } = this.props;

    return (
      <YearView
        values={this.buildCalendarValues()}
        onNextPageBtnClick={this.switchToNextPage}
        onPrevPageBtnClick={this.switchToPrevPage}
        onValueClick={this.handleChange}
        onBlur={this.handleBlur}
        inline={inline}
        onMount={onCalendarViewMount}
        hoveredItemIndex={this.state.hoveredCellPosition}
        onCellHover={this.onHoveredCellPositionChange}
        hasPrevPage={this.isPrevPageAvailable()}
        hasNextPage={this.isNextPageAvailable()}
        disabledItemIndexes={this.getDisabledPositions()}
        activeItemIndex={this.getActiveCellPosition()}
        onHeaderClick={onHeaderClick}
        localization={localization}
        pickerWidth={pickerWidth}
        pickerStyle={pickerStyle}
      />
    );
  }

  protected buildCalendarValues(): string[] {
    /*
      Return array of years (strings) like ['2012', '2013', ...]
      that used to populate calendar's page.
    */
    const years = [];
    const date = this.state.date;
    const padd = date.year() % YEARS_ON_PAGE;
    const firstYear = date.year() - padd;
    for (let i = 0; i < YEARS_ON_PAGE; i++) {
      years[i] = (firstYear + i).toString();
    }

    return years;
  }

  protected getInitialDatePosition(): number {
    const selectable = this.getSelectableCellPositions();
    const values = this.buildCalendarValues();
    const currentYearIndex = values.indexOf(this.state.date.year().toString());
    if (selectable.indexOf(currentYearIndex) < 0) {
      return selectable[0]!;
    }

    return currentYearIndex;
  }

  protected getActiveCellPosition(): number | undefined {
    /*
      Return position of a year that should be displayed as active
      (position in array returned by `this.buildCalendarValues`).
    */
    if (!isNil(this.props.value)) {
      const years = this.buildCalendarValues();
      const yearIndex = years.indexOf(this.props.value.year().toString());
      if (yearIndex >= 0) {
        return yearIndex;
      }
    }
  }

  protected getSelectableCellPositions(): number[] {
    return filter(
      range(0, YEARS_ON_PAGE),
      (y) => !includes(this.getDisabledPositions() as number[], y),
    );
  }

  protected getDisabledPositions(): number[] | undefined {
    /*
      Return position numbers of years that should be displayed as disabled
      (position in array returned by `this.buildCalendarValues`).
    */
    let disabled: number[] = [];
    const years = this.buildCalendarValues();
    if (isArray(this.props.enable)) {
      const enabledYears = this.props.enable.map((yearMoment) =>
        yearMoment.year().toString(),
      );
      disabled = concat(
        disabled,
        years
          .filter((year) => !includes(enabledYears, year))
          .map((year) => years.indexOf(year)),
      );
    }
    if (isArray(this.props.disable)) {
      disabled = concat(
        disabled,
        this.props.disable
          .filter((yearMoment) => includes(years, yearMoment.year().toString()))
          .map((yearMoment) => years.indexOf(yearMoment.year().toString())),
      );
    }
    if (!isNil(this.props.maxDate)) {
      if (parseInt(first(years)!, 10) > this.props.maxDate.year()) {
        disabled = range(0, years.length);
      } else if (includes(years, this.props.maxDate.year().toString())) {
        disabled = concat(
          disabled,
          range(
            years.indexOf(this.props.maxDate.year().toString()) + 1,
            years.length,
          ),
        );
      }
    }
    if (!isNil(this.props.minDate)) {
      if (parseInt(last(years)!, 10) < this.props.minDate.year()) {
        disabled = range(0, years.length);
      } else if (includes(years, this.props.minDate.year().toString())) {
        disabled = concat(
          disabled,
          range(0, years.indexOf(this.props.minDate.year().toString())),
        );
      }
    }
    if (disabled.length > 0) {
      return uniq(disabled);
    }
  }

  protected isNextPageAvailable(): boolean {
    const { maxDate, enable } = this.props;
    const lastOnPage = parseInt(last(this.buildCalendarValues())!, 10);

    if (isArray(enable)) {
      return some(enable, (enabledYear) => enabledYear.year() > lastOnPage);
    }
    if (isNil(maxDate)) {
      return true;
    }

    return lastOnPage < maxDate.year();
  }

  protected isPrevPageAvailable(): boolean {
    const { minDate, enable } = this.props;
    const firstOnPage = parseInt(first(this.buildCalendarValues())!, 10);

    if (isArray(enable)) {
      return some(enable, (enabledYear) => enabledYear.year() < firstOnPage);
    }
    if (isNil(minDate)) {
      return true;
    }

    return firstOnPage > minDate.year();
  }

  protected handleChange = (
    e: React.SyntheticEvent<HTMLElement> | null,
    { value }: { value: string },
  ): void => {
    const data: YearPickerOnChangeData = {
      ...this.props,
      value: { year: parseInt(value, 10) },
    };
    this.props.onChange(e, data);
  };

  protected switchToNextPage = (
    e: React.SyntheticEvent<HTMLElement> | null | undefined,
    data: any,
    callback?: () => void,
  ): void => {
    this.setState(({ date }) => {
      const nextDate = date.clone();
      nextDate.add(YEARS_ON_PAGE, "year");

      return { date: nextDate };
    }, callback);
  };

  protected switchToPrevPage = (
    e: React.SyntheticEvent<HTMLElement> | null | undefined,
    data: any,
    callback?: () => void,
  ): void => {
    this.setState(({ date }) => {
      const prevDate = date.clone();
      prevDate.subtract(YEARS_ON_PAGE, "year");

      return { date: prevDate };
    }, callback);
  };
}

export default YearPicker;
